/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { trpc } from "@/lib/trpc";

type ThemeMode = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

/** Accent color pair stored as hex strings */
export interface AccentColors {
  teal: string;   // e.g. "#00455F"
  gold: string;   // e.g. "#D4A017"
}

const DEFAULT_ACCENTS: AccentColors = {
  teal: "#00455F",
  gold: "#D4A017",
};

interface ThemeContextType {
  /** The user's chosen mode: light, dark, or system */
  mode: ThemeMode;
  /** The resolved theme after applying system preference */
  theme: ResolvedTheme;
  /** Update the theme mode (persists to server) */
  setTheme: (mode: ThemeMode) => void;
  /** Convenience: true when resolved theme is dark */
  isDark: boolean;
  /** Current accent colors */
  accents: AccentColors;
  /** Update accent colors (persists to server) */
  setAccents: (colors: Partial<AccentColors>) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return mode;
}

/**
 * Convert a hex color to an approximate OKLCH CSS string.
 * Uses sRGB → linear → OKLab → OKLCH conversion.
 */
function hexToOklch(hex: string): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;

  // sRGB → linear
  const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const lr = toLinear(r), lg = toLinear(g), lb = toLinear(b);

  // Linear sRGB → OKLab (using Björn Ottosson's matrix)
  const l_ = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m_ = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s_ = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);

  const L = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
  const bOk = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;

  // OKLab → OKLCH
  const C = Math.sqrt(a * a + bOk * bOk);
  let H = (Math.atan2(bOk, a) * 180) / Math.PI;
  if (H < 0) H += 360;

  return `oklch(${L.toFixed(3)} ${C.toFixed(3)} ${H.toFixed(1)})`;
}

/** Derive muted and foreground variants from a base hex color */
function deriveAccentVariants(hex: string, isDark: boolean) {
  const base = hexToOklch(hex);
  // Parse the oklch values to derive variants
  const match = base.match(/oklch\(([\d.]+)\s+([\d.]+)\s+([\d.]+)\)/);
  if (!match) return { base, foreground: "oklch(0.99 0 0)", muted: "oklch(0.95 0.02 90)" };

  const L = parseFloat(match[1]);
  const C = parseFloat(match[2]);
  const H = parseFloat(match[3]);

  if (isDark) {
    // Dark mode: lighten the base, dark foreground, dark muted bg
    return {
      base: `oklch(${Math.min(L + 0.15, 0.85).toFixed(3)} ${C.toFixed(3)} ${H.toFixed(1)})`,
      foreground: `oklch(0.12 0.01 ${H.toFixed(1)})`,
      muted: `oklch(${(0.22 + C * 0.1).toFixed(3)} ${(C * 0.4).toFixed(3)} ${H.toFixed(1)})`,
    };
  } else {
    // Light mode: use base as-is, white foreground, very light muted bg
    return {
      base: `oklch(${L.toFixed(3)} ${C.toFixed(3)} ${H.toFixed(1)})`,
      foreground: `oklch(0.99 0 0)`,
      muted: `oklch(${Math.min(0.95, 0.94 + C * 0.1).toFixed(3)} ${(C * 0.25).toFixed(3)} ${H.toFixed(1)})`,
    };
  }
}

/** Apply accent CSS custom properties to :root */
function applyAccentVars(accents: AccentColors, isDark: boolean) {
  const root = document.documentElement;
  const teal = deriveAccentVariants(accents.teal, isDark);
  const gold = deriveAccentVariants(accents.gold, isDark);

  root.style.setProperty("--accent-teal", teal.base);
  root.style.setProperty("--accent-teal-foreground", teal.foreground);
  root.style.setProperty("--accent-teal-muted", teal.muted);
  root.style.setProperty("--accent-gold", gold.base);
  root.style.setProperty("--accent-gold-foreground", gold.foreground);
  root.style.setProperty("--accent-gold-muted", gold.muted);
}

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultMode?: ThemeMode;
}

export function ThemeProvider({
  children,
  defaultMode = "light",
}: ThemeProviderProps) {
  // Load initial mode from localStorage (instant, no flash) then sync from server
  const [mode, setModeState] = useState<ThemeMode>(() => {
    try {
      const stored = localStorage.getItem("theme_mode");
      if (stored === "light" || stored === "dark" || stored === "system") return stored;
    } catch { /* localStorage unavailable */ }
    return defaultMode;
  });

  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(mode));

  // Accent colors — load from localStorage first, then sync from server
  const [accents, setAccentsState] = useState<AccentColors>(() => {
    try {
      const stored = localStorage.getItem("accent_colors");
      if (stored) return JSON.parse(stored) as AccentColors;
    } catch { /* localStorage unavailable */ }
    return DEFAULT_ACCENTS;
  });

  // Fetch server-side theme preference (non-blocking)
  const { data: serverTheme } = trpc.settings.getTheme.useQuery(undefined, {
    staleTime: 60_000,
    retry: false,
  });

  // Fetch server-side accent colors
  const { data: serverAccents } = trpc.settings.getAccentColors.useQuery(undefined, {
    staleTime: 60_000,
    retry: false,
  });

  // Sync theme from server on first load (server wins if different)
  useEffect(() => {
    if (serverTheme && (serverTheme === "light" || serverTheme === "dark" || serverTheme === "system")) {
      setModeState(serverTheme as ThemeMode); // eslint-disable-line react-hooks/set-state-in-effect -- intentional: server preference overrides local
    }
  }, [serverTheme]);

  // Sync accents from server
  useEffect(() => {
    if (serverAccents) {
      const merged = { ...DEFAULT_ACCENTS, ...serverAccents };
      setAccentsState(merged); // eslint-disable-line react-hooks/set-state-in-effect -- intentional: server preference overrides local
    }
  }, [serverAccents]);

  // Persist theme to server
  const setThemeMutation = trpc.settings.setTheme.useMutation();
  const setAccentsMutation = trpc.settings.setAccentColors.useMutation();

  const setTheme = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    try { localStorage.setItem("theme_mode", newMode); } catch { /* localStorage unavailable */ }
    setThemeMutation.mutate({ theme: newMode });
  }, [setThemeMutation]);

  const setAccents = useCallback((colors: Partial<AccentColors>) => {
    setAccentsState(prev => {
      const next = { ...prev, ...colors };
      try { localStorage.setItem("accent_colors", JSON.stringify(next)); } catch { /* localStorage unavailable */ }
      setAccentsMutation.mutate(next);
      return next;
    });
  }, [setAccentsMutation]);

  // Resolve theme when mode changes or system preference changes
  useEffect(() => {
    setResolved(resolveTheme(mode)); // eslint-disable-line react-hooks/set-state-in-effect -- intentional: derive resolved from mode

    if (mode === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = (e: MediaQueryListEvent) => {
        setResolved(e.matches ? "dark" : "light");
      };
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
  }, [mode]);

  // Apply dark class to document
  useEffect(() => {
    const root = document.documentElement;
    if (resolved === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    try { localStorage.setItem("theme_mode", mode); } catch { /* localStorage unavailable */ }
  }, [resolved, mode]);

  // Apply accent CSS variables whenever accents or theme changes
  useEffect(() => {
    applyAccentVars(accents, resolved === "dark");
  }, [accents, resolved]);

  return (
    <ThemeContext.Provider value={{ mode, theme: resolved, setTheme, isDark: resolved === "dark", accents, setAccents }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}
